/**
 * Browse Steam playtests — INVENTORY-DESKTOP §4 row 4 (lane D4). Prototype
 * lines 957-1001. Mounted by `DesktopApp` while `s.ptBrowseOpen`.
 *
 * `selectors.3e#ptBrowseVM` unchanged; `selectors.d4#ptBrowseVMDesktop` wraps it
 * for the longer desktop header (`…, APPID + STORE LINK COME WITH EVERY ROW`).
 *
 * `☆ TRACK` does not track directly — it routes through `ptFromBrowse`, which
 * pre-fills the add modal with a synthetic "widget found" result so the
 * party-size gate still has to be answered, exactly as the footer promises
 * ("TRACKING ASKS FOR PARTY SIZE, THEN ADDS IT TO EVERYONE'S PLAYTESTS TAB").
 * An already-tracked row toasts `ALREADY TRACKED` instead.
 *
 * DESKTOP LAYOUT (§4): 880px, the header note wraps, and each row is ONE line —
 * 120px art · `flex:1 min-200` body · TRACK · REQUEST ↗ (mobile stacked art
 * over chips over a 2-button row).
 *
 * INERT CHROME, reproduced from the prototype (lines 968-971): the two accent
 * chips and the sort pill have no handler and no data behind them in the
 * prototype either — `PT_BROWSE` is four hard-coded rows and one of them is
 * INVITE ONLY, so `OPEN SIGNUPS ✕` is decoration, not state. Kept verbatim on
 * the same grounds as the header's `2,143` vs the footer's `212` (INVENTORY §12
 * item 8: frozen copy is reproduced, contradictions included) and recorded in
 * docs/porting/D4.md. Deliberately NOT given `cursor:pointer`.
 */

import { PtCover } from '../../components/PtArt.tsx';
import { SearchBox } from '../../components/SearchBox.tsx';
import { useQd } from '../../store/index.ts';
import { ptBrowseVMDesktop } from '../../store/selectors.d4.ts';
import { Modal } from '../components/Modal.tsx';
import type { CSSProperties } from 'react';

const MONO = 'var(--gt-font-mono)';

/** Header notes are mono 10.5px `--gt-muted` in the prototype (§4). */
const NOTE: CSSProperties = { fontSize: 10, color: 'var(--gt-muted)' };

/** The two inert filter chips (prototype 968-969). */
const DEAD_CHIP: CSSProperties = {
  border: '1px solid var(--acc)',
  color: 'var(--acc)',
  background: 'var(--accSoft)',
  padding: '6px 9px',
};

export default function PtBrowseModal(): JSX.Element {
  const s = useQd();
  const v = ptBrowseVMDesktop(s);

  return (
    <Modal
      label="Browse steam modal"
      width={880}
      onClose={s.closePtBrowse}
      header={{ title: '⊞ BROWSE STEAM PLAYTESTS', titleClr: 'var(--gt-text)', note: <span style={NOTE}>{v.head}</span> }}
    >
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          padding: '14px 18px 0',
          flexWrap: 'wrap',
          fontFamily: MONO,
          fontSize: '10.5px',
          letterSpacing: '.04em',
        }}
      >
        <SearchBox
          value={v.q}
          onChange={(e) => s.setPtBq(e.target.value)}
          placeholder="SEARCH THE CATALOG…"
          w="220px"
          ifs={10.5}
        />
        <span style={DEAD_CHIP}>OPEN SIGNUPS ✕</span>
        <span style={DEAD_CHIP}>HIDE TRACKED ✕</span>
        <div style={{ flex: 1 }} />
        <span style={{ border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-3)', padding: '6px 9px' }}>
          SORT: TESTING NOW ▾
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 18px 0' }}>
        {v.rows.map((b) => (
          <div
            key={b.key}
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              border: '1px solid var(--gt-line)',
              background: 'var(--gt-tile)',
              padding: '10px 12px',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ position: 'relative', width: 120, aspectRatio: '460/215', flex: 'none', background: b.fall, overflow: 'hidden' }}>
              <PtCover style={{ position: 'absolute', inset: 0 }} fs={10} id={'br' + b.appid} ini={b.ini} cands={b.cands} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minWidth: 200 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--gt-text)' }}>{b.name}</div>
                <div style={{ fontSize: '10.5px', color: 'var(--gt-text-5)' }}>{b.dev}</div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {b.chips.map((ch) => (
                  <span
                    key={ch.key}
                    style={{
                      fontFamily: MONO,
                      fontSize: '8.5px',
                      letterSpacing: '.06em',
                      border: '1px solid ' + ch.bd,
                      color: ch.c,
                      background: ch.bg,
                      padding: '3px 6px',
                    }}
                  >
                    {ch.t}
                  </span>
                ))}
              </div>
              <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.03em', color: 'var(--gt-muted)' }}>{b.meta}</div>
            </div>

            <div
              onClick={() => {
                if (b.tracked) s.toast('ALREADY TRACKED');
                else s.ptFromBrowse(b.row);
              }}
              style={{
                border: '1px solid ' + b.tBd,
                color: b.tClr,
                background: b.tBg,
                fontFamily: MONO,
                fontSize: 10,
                padding: '8px 11px',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              }}
            >
              {b.tTxt}
            </div>

            {b.hasReq ? (
              <div
                onClick={() => window.open(b.reqUrl, '_blank')}
                style={{
                  background: 'var(--acc)',
                  color: 'var(--gt-ink)',
                  fontFamily: MONO,
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '9px 11px',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                }}
              >
                REQUEST ↗
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <div style={{ padding: '12px 18px 16px', fontFamily: MONO, fontSize: 9, letterSpacing: '.03em', color: 'var(--gt-muted-2)' }}>
        {v.foot}
      </div>
    </Modal>
  );
}
